import {DateTime, Diagram, DiagramType, nominate} from "platformed-client/types";

// Procure-to-Pay (P2P) Process Diagram
// Demonstrates purchasing and payment process with segregation of duties across 7 roles
export const procureToPay: Diagram = {
  diagram_id: nominate("DiagramId", "hardcoded-p2p-001"),
  name: "Procure-to-Pay (P2P) Process",
  description:
    "Purchasing and payment controls with segregation of duties across requisition, procurement, receiving, invoice processing, and payment authorization. Demonstrates proper three-way matching and approval thresholds required for SOX compliance.",
  diagram_type: "ProcessFlow" as DiagramType,
  created_at: "2025-09-18T10:30:00Z" as DateTime,
  updated_at: "2025-11-10T14:22:00Z" as DateTime,
  owner: null,
  deleted: null,
  lanes_enabled: false,
  lanes: {},
  generation_state: {type: "NotStarted"},

  nodes: {
    requisition: {
      diagram_node_id: nominate("DiagramNodeId", "p2p-requisition"),
      label: "Purchase Requisition",
      description: "Employee creates purchase requisition for needed goods/services",
      icon: "isoflow.document",
      position: [2, 2], // Requestor swimlane
    },
    reqApproval: {
      diagram_node_id: nominate("DiagramNodeId", "p2p-req-approval"),
      label: "Requisition Approval",
      description:
        "Manager reviews and approves requisition based on budget\n\nControls\nAP-01 Purchase Requisition Manager Approval",
      icon: "isoflow.block",
      position: [10, 2], // Approving Manager swimlane
    },
    vendorPO: {
      diagram_node_id: nominate("DiagramNodeId", "p2p-vendor-po"),
      label: "Vendor Selection & PO",
      description: "Procurement team selects vendor and creates purchase order",
      icon: "isoflow.user",
      position: [18, 2], // Procurement Team swimlane
    },
    poApproval: {
      diagram_node_id: nominate("DiagramNodeId", "p2p-po-approval"),
      label: "PO Approval",
      description:
        "Purchasing manager approves PO based on authorization limits\n\nControls\nAP-02 Purchase Order Approval Limits",
      icon: "isoflow.block",
      position: [18, 9], // Procurement Team swimlane (lower position)
    },
    goodsReceipt: {
      diagram_node_id: nominate("DiagramNodeId", "p2p-goods-receipt"),
      label: "Goods Receipt",
      description:
        "Receiving department inspects and records goods receipt\n\nControls\nAP-03 Independent Verification of Goods Received",
      icon: "isoflow.server",
      position: [26, 2], // Receiving Department swimlane
    },
    invoice: {
      diagram_node_id: nominate("DiagramNodeId", "p2p-invoice"),
      label: "Invoice Entry",
      description: "AP team enters vendor invoice into system",
      icon: "isoflow.document",
      position: [34, 2], // Accounts Payable swimlane
    },
    threeWayMatch: {
      diagram_node_id: nominate("DiagramNodeId", "p2p-three-way"),
      label: "Three-Way Match",
      description: "System matches PO, goods receipt, and invoice\n\nControls\nAP-04 Three-Way Match Requirement",
      icon: "isoflow.document",
      position: [34, 9], // Accounts Payable swimlane (lower)
    },
    invoiceApproval: {
      diagram_node_id: nominate("DiagramNodeId", "p2p-invoice-approval"),
      label: "Invoice Approval",
      description: "Designated approver confirms accuracy and approves payment",
      icon: "isoflow.block",
      position: [34, -2], // Accounts Payable swimlane (upper)
    },
    payment: {
      diagram_node_id: nominate("DiagramNodeId", "p2p-payment"),
      label: "Payment Processing",
      description: "Treasury prepares payment (check, ACH, wire)",
      icon: "isoflow.document",
      position: [42, 2], // Treasury/Payment Auth swimlane
    },
    paymentAuth: {
      diagram_node_id: nominate("DiagramNodeId", "p2p-payment-auth"),
      label: "Payment Authorization",
      description: "Authorized signer releases payment to vendor\n\nControls\nAP-05 Final Payment Authorization",
      icon: "isoflow.block",
      position: [42, 8], // Treasury/Payment Auth swimlane (lower)
    },
    glPosting: {
      diagram_node_id: nominate("DiagramNodeId", "p2p-gl-posting"),
      label: "GL Posting",
      description: "Payment recorded in general ledger and vendor account updated",
      icon: "fi.storage.015.book",
      position: [50, 2], // General Ledger swimlane
    },
  },

  connections: {
    "p2p-1": {
      diagram_connection_id: nominate("DiagramConnectionId", "p2p-conn-1"),
      source_node_id: nominate("DiagramNodeId", "p2p-requisition"),
      target_node_id: nominate("DiagramNodeId", "p2p-req-approval"),
      label: "Requisition",
      color: "blue",
      anchors: [],
    },
    "p2p-2": {
      diagram_connection_id: nominate("DiagramConnectionId", "p2p-conn-2"),
      source_node_id: nominate("DiagramNodeId", "p2p-req-approval"),
      target_node_id: nominate("DiagramNodeId", "p2p-vendor-po"),
      label: "Approved Requisition",
      color: "green",
      anchors: [],
    },
    "p2p-3": {
      diagram_connection_id: nominate("DiagramConnectionId", "p2p-conn-3"),
      source_node_id: nominate("DiagramNodeId", "p2p-vendor-po"),
      target_node_id: nominate("DiagramNodeId", "p2p-po-approval"),
      label: "PO for Approval",
      color: "blue",
      anchors: [],
    },
    "p2p-4": {
      diagram_connection_id: nominate("DiagramConnectionId", "p2p-conn-4"),
      source_node_id: nominate("DiagramNodeId", "p2p-po-approval"),
      target_node_id: nominate("DiagramNodeId", "p2p-goods-receipt"),
      label: "Approved PO",
      color: "green",
      anchors: [],
    },
    "p2p-5": {
      diagram_connection_id: nominate("DiagramConnectionId", "p2p-conn-5"),
      source_node_id: nominate("DiagramNodeId", "p2p-goods-receipt"),
      target_node_id: nominate("DiagramNodeId", "p2p-invoice"),
      label: "Goods Receipt Doc",
      color: "purple",
      anchors: [],
    },
    "p2p-6": {
      diagram_connection_id: nominate("DiagramConnectionId", "p2p-conn-6"),
      source_node_id: nominate("DiagramNodeId", "p2p-invoice"),
      target_node_id: nominate("DiagramNodeId", "p2p-three-way"),
      label: "Invoice",
      color: "blue",
      anchors: [],
    },
    "p2p-7": {
      diagram_connection_id: nominate("DiagramConnectionId", "p2p-conn-7"),
      source_node_id: nominate("DiagramNodeId", "p2p-three-way"),
      target_node_id: nominate("DiagramNodeId", "p2p-invoice-approval"),
      label: "Matched Invoice",
      color: "green",
      anchors: [],
    },
    "p2p-8": {
      diagram_connection_id: nominate("DiagramConnectionId", "p2p-conn-8"),
      source_node_id: nominate("DiagramNodeId", "p2p-invoice-approval"),
      target_node_id: nominate("DiagramNodeId", "p2p-payment"),
      label: "Approved Invoice",
      color: "green",
      anchors: [],
    },
    "p2p-9": {
      diagram_connection_id: nominate("DiagramConnectionId", "p2p-conn-9"),
      source_node_id: nominate("DiagramNodeId", "p2p-payment"),
      target_node_id: nominate("DiagramNodeId", "p2p-payment-auth"),
      label: "Payment for Auth",
      color: "blue",
      anchors: [],
    },
    "p2p-10": {
      diagram_connection_id: nominate("DiagramConnectionId", "p2p-conn-10"),
      source_node_id: nominate("DiagramNodeId", "p2p-payment-auth"),
      target_node_id: nominate("DiagramNodeId", "p2p-gl-posting"),
      label: "Authorized Payment",
      color: "green",
      anchors: [],
    },
  },

  regions: {},
  engagement: {
    engagement_id: nominate("EngagementId", "p2p-engagement-1"),
    name: "",
    third_party: {name: "", third_party_id: nominate("ThirdPartyId", "p2p-third-party-1")},
  },
};
